import { FC, useEffect } from 'react';
import { useProjectSettings } from 'frontastic';
import ServiceHighlightsCard from './components/card';
import PromiseBanner from './components/PromiseBanner';
import { BrandPromiseProps, Card } from './types';

const BrandPromise: FC<BrandPromiseProps> = ({ ...props }) => {
  const {
    cards,
    promiseImages,
    titleTextColor,
    subTitleTextColor,
  } = props;
  const orderClasses = ['order-2 lg:order-1', 'order-3 lg:order-2', 'order-4 lg:order-3'];
  const { projectSettings } = useProjectSettings();

  // without this useEffect, a user will be scrolled to the top after submitting the kalviyo form on iOS devices.
  // iOS Safari handles virtual keyboard dismissals and DOM re-renders very aggressively
  // When an <input> loses focus on iOS (which happens the instant the user taps "Submit" and the keyboard closes),
  // Safari automatically shifts or resets the viewport scroll offset
  // By explicitly blurring the active input before Klaviyo handles the submit event, iOS
  // cleanly dismisses the keyboard and locks the scroll position
  useEffect(() => {
    const handleKlaviyoForms = (event: Event) => {
      const customEvent = event as CustomEvent<{ type?: string }>;
      if (customEvent.detail?.type === 'submit') {
        // Dismiss the keyboard instantly when submit is clicked
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    };

    window.addEventListener('klaviyoForms', handleKlaviyoForms);
    return () => {
      window.removeEventListener('klaviyoForms', handleKlaviyoForms);
    };
  }, []);
  
  return (
    <div className="mx-24 whitespace-normal break-words sm:mx-24 md:mx-75 lg:mx-76 pb-20 xl:mx-0">
      <div className="container relative w-full" data-ffid="ctr-brand-promise-banner">
        <PromiseBanner images={promiseImages} {...props} />
        <div className="flex flex-1 flex-col gap-24 sm:flex-row">
          {cards?.map((card: Card, index: number) => (
            <ServiceHighlightsCard
              key={`${card?.title}-${index}`}
              titleTextColor={titleTextColor}
              subTitleColor={subTitleTextColor}
              className={orderClasses[index]}
              index={index}
              {...props}
              {...card}
            />
          ))}
        </div>
        <div>
          {/* Do not remove this, in future we may use this.
           <EmailPromoContainer imageSrc={subscribeImageSrc} {...props} /> 
           */}
          <div className="mt-25">
            <div data-ffid="ctr-klaviyo-brand-promise-signup-form" className={projectSettings?.KLAVIO_FORM_KEY as string ?? ''}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandPromise;
